import type { ReactNode } from 'react';
import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

export interface BoardColumnProps {
  /** Written to `data-column`, so a test can name the column it means. */
  id: string;
  label: string;
  /** Shown in the header pill. The caller's total, not the children's length. */
  count: number;
  /** Accent for the underline and the count pill. */
  accent: { bg: string; color: string };
  /**
   * A warning under the heading, when this column holds work that has gone
   * wrong — "2 com aluguel em atraso". Already localized and already
   * counted; the column only paints it.
   *
   * Only pass it where the count is honest. The column renders every card
   * its board handed over, so on a paged board this would describe the
   * loaded slice while reading as a fact about the column.
   */
  alert?: string;
  /** Icon for the empty placeholder, sized by the caller. */
  emptyIcon: ReactNode;
  emptyLabel: string;
  /** The cards. An empty array draws the placeholder instead. */
  children: ReactNode;
  isEmpty: boolean;
}

/**
 * Empty-column placeholder: an icon in a tinted circle above the label.
 *
 * Kept visually identical to the attendance board's: a broker moving
 * between Atendimentos, Locações and Vendas should not have to re-learn
 * what an empty column looks like.
 */
function EmptyColumnState({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-(--space-2) rounded-(--radius-md) border border-dashed border-(--border-subtle) bg-(--surface-secondary) p-(--space-6)">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-full"
        style={{ background: 'color-mix(in srgb, var(--text-tertiary) 8%, transparent)' }}
      >
        {icon}
      </div>
      <Text
        component="span"
        className="text-[length:var(--text-meta)] [font-weight:var(--font-medium)] text-(--text-tertiary)"
      >
        {label}
      </Text>
    </div>
  );
}

/**
 * One board column: an accented header with a count, then its cards.
 *
 * Extracted from the attendance board so Locações and Vendas get the same
 * column rather than a third and fourth hand-built lookalike. It stays
 * presentational on purpose — no data, no paging, no "load more": those
 * boards hold every record the page already fetched, so a column that
 * asked for more would have nothing to ask.
 */
export function BoardColumn({
  id, label, count, accent, alert, emptyIcon, emptyLabel, isEmpty, children,
}: BoardColumnProps) {
  return (
    <div
      data-testid="board-column"
      data-column={id}
      className={cn(
        'flex flex-col w-full min-w-0',
        'rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-primary) shadow-sm overflow-hidden',
      )}
    >
      <div className="flex flex-col gap-(--space-1-5) p-(--space-3)">
      <div className="flex items-center gap-(--space-2)">
        <Text
          component="span"
          className="inline-block pb-(--space-1) text-[length:var(--text-body-sm)] [font-weight:var(--font-bold)] text-(--text-primary) whitespace-nowrap"
          style={{ borderBottom: `2px solid ${accent.color}` }}
        >
          {label}
        </Text>
        <Text
          component="span"
          data-testid="board-column-count"
          className="shrink-0 rounded-(--radius-sm) px-(--space-2) py-(--space-1) text-[length:var(--text-meta)] [font-weight:var(--font-bold)]"
          style={{ background: accent.bg, color: accent.color }}
        >
          {count}
        </Text>
      </div>

      {alert && (
        <Text
          component="span"
          data-testid="board-column-alert"
          className={cn(
            'text-[length:var(--text-meta)] [font-weight:var(--font-semibold)]',
            'text-(--accent-cobre)',
          )}
        >
          {alert}
        </Text>
      )}
      </div>

      <div className="flex flex-col gap-(--space-2) p-(--space-2) min-h-[80px]">
        {isEmpty ? <EmptyColumnState icon={emptyIcon} label={emptyLabel} /> : children}
      </div>
    </div>
  );
}

export default BoardColumn;
